import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './WhatsMyZodiac.module.css';
import GlobalHeader from '../GlobalHeader';
import { useAuthContext } from '../../hooks/useAuthContext';

interface QuestionChoice {
  text: string;
  group: string[];
}

interface Question {
  id: string;
  scenario: string;
  choices: QuestionChoice[];
}

const zodiacAnimals = [
  'Light Bear', 'Dark Bear', 'Light Crane', 'Dark Crane', 'Light Frog', 'Dark Frog',
  'Light Fungi', 'Dark Fungi', 'Light Goat', 'Dark Goat', 'Light Monkey', 'Dark Monkey',
  'Light Octopus', 'Dark Octopus', 'Light Penguin', 'Dark Penguin', 'Light Rhino', 'Dark Rhino',
  'Light Snake', 'Dark Snake', 'Light Tree', 'Dark Tree', 'Light Wolf', 'Dark Wolf',
  'Light Virus', 'Dark Virus'
];

// Custom descriptions for specific quiz outcomes
const customDescriptions: { [key: string]: string } = {
  'Dark Octopus': 'Like a solitary octopus, you like to infiltrate in the shadows! Using intellect and misdirection to beguile and smother your opponents!',
  'Dark Tree': 'Like an ancient tree with deep roots, you understand the power of patience and position! You excel at making others come to you, forcing them into mistakes through your unwavering defensive presence!',
  'Dark Virus': 'Like a Virus you believe in destruction through cruel and swift means, protecting those around you means nothing to you as you annihilate the battle field',
  'Light Bear': 'Like a protective bear, you are the guardian who stands between danger and those you care about! You excel at creating safe spaces and shielding others through your steadfast presence!',
  'Light Tree': 'Like a nurturing tree, you provide shelter and support for others to thrive! You excel at creating connections and helping allies reach their full potential through your supportive nature!',
  'Light Rhino': 'Like a charging rhino, you believe in decisive action and strategic positioning! You excel at breaking through obstacles and creating opportunities through your unwavering determination!',
  'Light Fungi': 'Like healing fungi, you bring calm balance and nourishment to those around you! You excel at supporting your teammates through gentle aid and are willing to sacrifice yourself for the greater good of the group!',
  'Light Snake': 'Like the snake who sheds its skin, you optmistically continue on renewed and fearless of the next path you will take',
  'Dark Goat': 'An agent of misdirection and chaos, you make others move to your empty threats and bait them in to a attack you planned all along',
  'Dark Fungi': 'Seeding spores in to the enemies mind, you dominate through a toxic control and sinister baiting your foes to attack, just to get the upper hand',
  'Dark Frog': 'Like a poisonous frog, you wield a double-edged sword of retribution! You hurt me, I hurt you back - coldly attacking while making enemies fear your poison, taking advantage of their hesitation to strike!',
  'Dark Wolf': 'Like a lone wolf, you embody solitary power and fierce independence! You excel at balancing lightning speed with ferocious strikes, hunting alone and trusting no one but your own instincts to survive!'
};

const getPieceImage = (fullName: string) => {
  const [variant, ...nameParts] = fullName.split(' ');
  const pieceName = nameParts.join(' ');
  return `/Icons/${pieceName}-${variant.toLowerCase()}.png`;
};

// Group narrowing system - each question narrows down the possible pieces
const questions: Question[] = [
  {
    id: 'decision-style',
    scenario: "As an unknown force approaches you...",
    choices: [
      {
        text: "Wait... patience and knowledge is the clearest path forward",
        group: [
          'Light Bear', 'Light Frog', 'Light Fungi', 'Light Octopus', 'Light Rhino',
          'Light Snake', 'Light Tree', 'Light Virus', 'Dark Crane', 'Dark Frog',
          'Dark Fungi', 'Dark Goat', 'Dark Octopus', 'Dark Penguin', 'Dark Tree',
          'Dark Wolf', 'Dark Virus'
        ]
      },
      {
        text: "Adapt on the fly to changes, neither late or rash",
        group: [
          'Light Bear', 'Light Fungi', 'Light Goat', 'Light Monkey', 'Light Octopus',
          'Light Rhino', 'Light Snake', 'Light Wolf', 'Dark Crane', 'Dark Frog',
          'Dark Fungi', 'Dark Goat', 'Dark Monkey', 'Dark Octopus', 'Dark Penguin',
          'Dark Snake', 'Dark Wolf', 'Dark Virus'
        ]
      },
      {
        text: "Act with immediacy, the first to act has the advantage",
        group: [
          'Light Crane', 'Light Goat', 'Light Monkey', 'Light Penguin', 'Light Rhino',
          'Light Snake', 'Light Wolf', 'Dark Bear', 'Dark Goat', 'Dark Monkey',
          'Dark Penguin', 'Dark Rhino', 'Dark Snake', 'Dark Virus'
        ]
      }
    ]
  },
  {
    id: 'leadership-style',
    scenario: "When working with others to achieve a goal, what's your preferred approach?",
    choices: [
      {
        text: "Too many chefs spoil the broth, a clear leader creates clarity and decisiveness",
        group: [
          'Light Crane', 'Light Goat', 'Light Penguin', 'Light Rhino', 'Light Wolf',
          'Dark Bear', 'Dark Monkey', 'Dark Penguin', 'Dark Snake', 'Dark Virus',
          'Light Bear', 'Light Tree', 'Dark Octopus', 'Dark Tree'
        ]
      },
      {
        text: "Influence the best outcome, strengthen your group and weaken others for the best outcome",
        group: [
          'Light Monkey', 'Light Snake', 'Dark Goat', 'Dark Penguin', 'Light Fungi',
          'Light Octopus', 'Dark Frog', 'Dark Fungi', 'Dark Octopus', 'Light Frog',
          'Light Virus', 'Dark Wolf', 'Dark Virus'
        ]
      },
      {
        text: "Work on the cohesion of the group, no one wins a war alone",
        group: [
          'Light Goat', 'Light Monkey', 'Light Rhino', 'Light Snake', 'Light Wolf',
          'Light Bear', 'Light Fungi', 'Light Octopus', 'Dark Frog', 'Dark Fungi',
          'Light Frog', 'Light Tree', 'Dark Tree'
        ]
      }
    ]
  },
  {
    id: 'risk-management',
    scenario: "When facing uncertain outcomes, how do you approach risk and reward?",
    choices: [
      {
        text: "If you have a good chance for a great outcome, take it! Fortune favours the bold",
        group: [
          'Light Crane', 'Light Fungi', 'Light Goat', 'Light Penguin', 'Light Snake',
          'Light Virus', 'Dark Bear', 'Dark Frog', 'Dark Fungi', 'Dark Goat',
          'Dark Monkey', 'Dark Octopus', 'Dark Rhino', 'Dark Snake', 'Dark Wolf',
          'Dark Virus'
        ]
      },
      {
        text: "Some managed risks is always the best, being flexible is being unpredictable",
        group: [
          'Light Bear', 'Light Crane', 'Light Fungi', 'Light Goat', 'Light Monkey',
          'Light Octopus', 'Light Rhino', 'Light Snake', 'Light Tree', 'Light Wolf',
          'Light Virus', 'Dark Crane', 'Dark Frog', 'Dark Fungi', 'Dark Goat',
          'Dark Octopus', 'Dark Snake', 'Dark Wolf', 'Dark Virus'
        ]
      },
      {
        text: "Taking a small loss is smarter if it saves you from a bigger risk, lose the battle, win the war",
        group: [
          'Light Bear', 'Light Frog', 'Light Octopus', 'Light Rhino', 'Light Tree',
          'Light Wolf', 'Dark Crane', 'Dark Penguin', 'Dark Tree'
        ]
      }
    ]
  },
  {
    id: 'tactical-approach',
    scenario: "At the final battle as you close in on victory you...",
    choices: [
      {
        text: "Hide in the shadows, misdirect, divide and conquer",
        group: [
          'Dark Octopus'
        ]
      },
      {
        text: "Eliminate them all, every last one",
        group: [
          'Dark Virus'
        ]
      }
    ]
  },
  {
    id: 'siege-strategy',
    scenario: "Outnumbered, your enemy will be here soon, best defence is to...",
    choices: [
      {
        text: "Prepare to fight, use every advantage you can for the coming fight",
        group: [
          'Light Bear', 'Light Tree', 'Light Rhino'
        ]
      },
      {
        text: "Escape, no point in taking an unfair fight now when you can tip the favour next time",
        group: [
          'Dark Octopus'
        ]
      },
      {
        text: "Strike first while they least expect it, eliminate them all",
        group: [
          'Dark Virus'
        ]
      }
    ]
  },
  {
    id: 'defensive-philosophy',
    scenario: "Playing a game with friends you notice you can win the whole game if you",
    choices: [
      {
        text: "Work with another and dominate through advantages you both share",
        group: ['Light Bear', 'Light Tree', 'Light Rhino']
      },
      {
        text: "You have found an exploit that allows you to not let your opponents move, eventually leading to your complete victory through an exploit",
        group: ['Dark Penguin', 'Dark Tree']
      },
    ]
  },
  {
    id: 'ally-protection',
    scenario: "A friend is being threatened...",
    choices: [
      {
        text: "Console, build your friend up and make them know you are there for them",
        group: [
          'Light Tree'
        ]
      },
      {
        text: "Protect your friend from mistreatment, stand in harms way for them",
        group: [
          'Light Bear'
        ]
      },
      {
        text: "Attack, find out who it is and find them and take them on directly",
        group: [
          'Light Rhino'
        ]
      }
    ]
  },
  {
    id: 'dark-strategy',
    scenario: "A stranger desperately wants something from you. You have also found out they hold a special secret, but they don't know you know",
    choices: [
      {
        text: "Charm and misdirect - use your innocent appearance to lure them into revealing more",
        group: [
          'Dark Penguin'
        ]
      },
      {
        text: "Play steadfast, if they want something from you, they will come and it will give you the upperhand in the negotiation",
        group: [
          'Dark Tree'
        ]
      }
    ]
  },
  {
    id: 'influence-strategy',
    scenario: "A stranger has been caught spreading harmless lies about the king whom you serve, he has gotten wind and wants her head, do you?",
    choices: [
      {
        text: "Misdirect the king to believe it was a foe you want targeted, saving this harmless woman at no cost",
        group: ['Dark Frog', 'Dark Fungi', 'Dark Virus']
      },
      {
        text: "Avoid the commotion, its a stranger and the will of the king, not your fight",
        group: ['Dark Octopus', 'Light Snake', 'Light Virus', 'Dark Wolf', 'Dark Goat']
      },
      {
        text: "Tell the king he is wrong to call for the death of someone over silly rumours, losing favor to save a life",
        group: ['Light Fungi']
      }
    ]
  },
  {
    id: 'poison-method',
    scenario: "A competitor has created a new invention that will put you out of the job and ruin you, you come across his invention in private do you?",
    choices: [
      {
        text: "Tamper with it so their presentation fails publicly",
        group: ['Dark Frog']
      },
      {
        text: "Steal it and move to another city, thriving off of their invention",
        group: ['Dark Fungi']
      },
      {
        text: "Burn it all, so you can continue to thrive where you are",
        group: ['Dark Virus']
      },
    ]
  },
  {
    id: 'battlefield-strategy',
    scenario: "You are in a battlefield hiding a potent and deadly vial, a powerful foe approaches...",
    choices: [
      {
        text: "Threaten to use the vial, it will create a stalemate and allow you to escape",
        group: ['Dark Octopus', 'Light Virus']
      },
      {
        text: "Hide amongst the fallen, survive to fight another day",
        group: ['Light Snake', 'Dark Goat']
      },
      {
        text: "Lurk in the shadows, attack with the vial and take out their strongest fighter",
        group: ['Dark Wolf']
      }
    ]
  },
  {
    id: 'cell-entrapment',
    scenario: "You're a prisoner of a travelling guard, your jailor is lagging behind to make underhanded deals with locals, stealing from his captain... as he falls asleep that night you realize he did not secure your shackles correctly and you can slip out... do you?",
    choices: [
      {
        text: "Place him in the shackles and leave him to rot, no one will find him in the middle of nowhere",
        group: ['Dark Octopus']
      },
      {
        text: "Take his weapon and wake him, tell him that he must tell his captain that he killed you trying to run away or you will tell the captain that he is stealing from him, even if it means you end back up in chains",
        group: ['Light Virus']
      }
    ]
  },
  {
    id: 'shame-return',
    scenario: "Long ago you left your friends in a time of need, ashamed you fled to another town, now you are set to return to the place that brought you shame, do you?",
    choices: [
      {
        text: "Own it, ask for forgiveness for the shame, be subservient until you can prove your worth and loyalty once again",
        group: ['Light Snake']
      },
      {
        text: "Hide it, its been forever, they may not even recognize you, deny you are the same person and avoid those who were closest to you then",
        group: ['Dark Goat']
      }
    ]
  },
  {
    id: 'advisor-dilemma',
    scenario: "I'm an advisor on a group and come across an offer that would massively benefit me and actually hurt my group if I convinced them to go through with it",
    choices: [
      {
        text: "Inform them that its an imbalanced deal and strike a new one that works for my group",
        group: ['Light Fungi', 'Light Snake', 'Light Virus']
      },
      {
        text: "Expose them for their plan and force them to pay for their deceit",
        group: ['Light Octopus', 'Dark Frog', 'Dark Fungi']
      },
      {
        text: "Tell them to take the offer and then leave, they willingly agreed, so you have no obligation",
        group: ['Dark Octopus', 'Dark Wolf', 'Dark Virus', 'Dark Goat']
      }
    ]
  },
  {
    id: 'altruistic-sacrifice',
    scenario: "Trekking through a dangerous cold mountain during a hailstorm a friend breaks his leg, it's bad. The leader and the others agree that we will all die if we try to carry him but you con't seem to let him go",
    choices: [
      {
        text: "Stay with him and try to create a camp, if you can move to a more sheltered space and keep you both warm, you might both survive until the storm passes",
        group: ['Light Fungi']
      },
      {
        text: "Carry him yourself, its a hard trek, but you are resilient and strong, you might make it to the next camp",
        group: ['Light Snake']
      },
      {
        text: "Threaten all the groups rations that you are carrying for them, unless they change their minds, its all of us or none of us",
        group: ['Light Virus']
      }
    ]
  },
  {
    id: 'justice-approach',
    scenario: "Sitting at a table in the next town, you overhear a man talking about ransacking your towns palace and burning it to the ground, you are an off duty guard sworn to protect it",
    choices: [
      {
        text: "Rush home to bar all the windows and doors and put extra guards on duty! He's not getting in tonight",
        group: ['Light Octopus']
      },
      {
        text: "Rush home to set up traps under all the doors and windows, we will find him by his shrieks!",
        group: ['Dark Frog']
      },
      {
        text: "Befriend the man and talk your way in to his good books, tell him you have been exiled and join him in his plan, once he is inside, turn on him when you have the upperhand in manpower!",
        group: ['Dark Fungi']
      }
    ]
  },
  {
    id: 'opportunistic-timing',
    scenario: "As a fugitive escaping two relentless guards, you come to a tall cliff and waterfall! You did not see the edge and fall but luckily land on a small concealed bank just below the cliffs edge, they are still searching for you and come to peer over the edge to see if you went over",
    choices: [
      {
        text: "I wait until night to climb back, they won't stop looking for me, but they'll have to sleep sometime",
        group: ['Dark Octopus', 'Dark Wolf']
      },
      {
        text: "As one peers over I throw them down over the edge! The fight continues but atleast its a fair fight now",
        group: ['Dark Virus']
      },
      {
        text: "I quickly throw a large stone and my clothes down to the water, they might see them and think I fell and give up looking for me",
        group: ['Dark Goat']
      }
    ]
  },
  {
    id: 'shadow-advantage',
    scenario: "You notice someone has been following you for hours, as the sun sets, you artfully slip in to cover and begin to tail them instead, do you?",
    choices: [
      {
        text: "Risk trying to incapacitate them and bind them with rope and find out what they are upto, its better to know their intentions",
        group: ['Dark Octopus']
      },
      {
        text: "Strike swiftly and decisively, take them out while you have the upperhand",
        group: ['Dark Wolf']
      }
    ]
  },
  {
    id: 'trust-building',
    scenario: "A new group member seems hesitant to fully trust the team. How do you help them feel included?",
    choices: [
      {
        text: "Be open and honest about your intentions, show them through consistent actions that they can trust you",
        group: ['Light Frog']
      },
      {
        text: "Patiently observe and understand their concerns, then methodically address each one with strategic solutions",
        group: ['Light Octopus']
      },
      {
        text: "Use charm and calculated moves to manipulate their emotions and get them to cooperate",
        group: ['Dark Penguin']
      }
    ]
  },
  {
    id: 'community-support',
    scenario: "Your community is facing a crisis that threatens everyone's wellbeing. What's your approach?",
    choices: [
      {
        text: "Focus on healing and nurturing - provide support to those who need it most and help the community recover naturally",
        group: ['Light Fungi', 'Dark Fungi']
      },
      {
        text: "Lead transformation and change - help the community adapt and evolve to emerge stronger than before",
        group: ['Light Snake', 'Dark Frog']
      }
    ]
  },
  {
    id: 'nature-connection',
    scenario: "You discover a wounded animal in the forest. How do you help?",
    choices: [
      {
        text: "Gently tend to its wounds with natural remedies, creating a healing environment where it can recover",
        group: ['Light Fungi']
      },
      {
        text: "Use whatever means necessary to control the situation, even if it means the animal becomes dependent on you",
        group: ['Dark Fungi']
      }
    ]
  },
  {
    id: 'transformation-method',
    scenario: "Your friend needs to make a major life change but is afraid. How do you help them?",
    choices: [
      {
        text: "Share wisdom from your own experiences and guide them through positive transformation step by step",
        group: ['Light Snake']
      },
      {
        text: "Force them to confront their fears through harsh truth-telling, even if it's painful - sometimes shock is needed",
        group: ['Dark Frog']
      }
    ]
  },
  // NEW ADAPTIVE BRANCH QUESTIONS (Q4-Q6)
  {
    id: 'adaptive-control-territorial-power',
    scenario: "You've been given authority over a crucial territory. A rival faction is encroaching, but direct confrontation risks escalating to full war. How do you maintain control?",
    choices: [
      {
        text: "Mobile defense - Keep your forces light and agile. React quickly to threats as they emerge, repositioning constantly. Sacrifice absolute control for adaptive presence and swift counterstrikes.",
        group: ['Light Goat']
      },
      {
        text: "Shadow manipulation - Spread information that makes the territory seem less valuable while secretly reinforcing its importance. Use misdirection, leverage relationships, and strategic positioning to outmaneuver without confronting.",
        group: ['Dark Monkey', 'Dark Octopus', 'Dark Snake', 'Dark Virus']
      }
    ]
  },
  {
    id: 'adaptive-manipulation-style',
    scenario: "Your manipulation must be precise. You've identified a key vulnerability in your rival's operations, but exploiting it requires choosing the RIGHT form of deception. What's your approach?",
    choices: [
      {
        text: "Clever mischief - Create chaos through unpredictable tricks and surprising maneuvers. Keep them off-balance with wit and audacity. They'll never know what's coming next because you adapt your deceptions on the fly.",
        group: ['Dark Monkey']
      },
      {
        text: "Shadow strategy - Work from the depths, controlling information and perception. Let them think they understand the situation while you pull strings they can't see. Patient, methodical, inevitable.",
        group: ['Dark Octopus']
      },
      {
        text: "Transformative deception - Change the very nature of the situation. Make them believe they want what you want, that your goals align with theirs. Not tricks or shadows - fundamental transformation of perspective.",
        group: ['Dark Snake']
      },
      {
        text: "Infectious spread - Plant ideas that replicate and spread through their organization like a virus. Let your manipulation become self-sustaining, carried forward by their own people without your continued intervention.",
        group: ['Dark Virus']
      }
    ]
  },
  {
    id: 'adaptive-influence-philosophy',
    scenario: "Your community faces a crisis of confidence. Morale is collapsing, trust eroding. People are looking to you for leadership, but what KIND of influence will you exert?",
    choices: [
      {
        text: "Life force leadership - Focus on the fundamental well-being of your people. Address their material needs, health, and vitality. Lead by ensuring everyone is taken care of - either through genuine healing or by controlling their dependencies.",
        group: ['Light Fungi', 'Dark Fungi']
      },
      {
        text: "Social persuasion - Lead through words, wisdom, and social dynamics. Change hearts and minds. Transform the community's perspective on the crisis. Guide them through understanding, whether it's genuine wisdom or poisonous rhetoric.",
        group: ['Light Snake', 'Dark Frog', 'Dark Goat']
      },
      {
        text: "Power projection - Lead through demonstration of capability. Show them you have the power to navigate this crisis. Whether through strategic brilliance, intimidating strength, or spreading your influence like an infection - prove you're the solution.",
        group: ['Dark Octopus', 'Dark Wolf', 'Dark Virus']
      }
    ]
  },
  {
    id: 'adaptive-life-force-nature',
    scenario: "You have the power to sustain life in this community. Your influence over their well-being is absolute - they depend on you. How do you wield this responsibility?",
    choices: [
      {
        text: "Selfless healing - Give freely without expectation. Your role is to nurture, heal, and support life unconditionally. Even when it costs you, even when they don't appreciate it. The reward is seeing them thrive independently.",
        group: ['Light Fungi']
      },
      {
        text: "Controlled dependency - Provide what they need, but ensure they remember who provides it. Your support comes with strings - loyalty, obedience, recognition. Not out of cruelty, but because leverage is survival. Help them, but own them.",
        group: ['Dark Fungi']
      }
    ]
  },
  {
    id: 'adaptive-social-transformation',
    scenario: "You've identified the root cause of the community's problems - a corrupt system maintained by complacency. You can influence change, but what KIND of transformation will you inspire?",
    choices: [
      {
        text: "Wisdom and renewal - Guide them toward genuine understanding. Help them see truth, shed old patterns, and transform through enlightenment. It's slower, requires patience, but creates lasting change. Like a snake shedding its skin.",
        group: ['Light Snake']
      },
      {
        text: "Retributive justice - Sometimes growth requires fear. Expose the corrupt, make examples, use shame and social pressure to punish wrongdoing. The poison that kills the infection. Transformation through consequences.",
        group: ['Dark Frog']
      },
      {
        text: "Chaos and misdirection - The system is rotten - burn it down through confusion. Spread contradictory information, create chaos, let them fight amongst themselves. New order emerges from collapse. Transform through destruction.",
        group: ['Dark Goat']
      }
    ]
  },
  {
    id: 'adaptive-power-methodology',
    scenario: "You've decided to lead through power - but power takes many forms. The crisis demands strength, but what kind of strength will you embody?",
    choices: [
      {
        text: "Shadow control - Operate from the background. Pull strings, manipulate variables, control outcomes without being seen. Your power is in knowing everything, being everywhere, and never being confronted directly. Strategic, calculated, fluid.",
        group: ['Dark Octopus']
      },
      {
        text: "Lone intimidation - Stand apart. Be the one who doesn't need anyone, who survives alone, who hunts solo. Your power is in your independence and your willingness to do what others won't. Predatory, intimidating, self-sufficient.",
        group: ['Dark Wolf']
      },
      {
        text: "Infectious influence - Spread like wildfire. Your ideas, your methods, your presence - they spread to others who spread to others. Power through exponential growth. You don't control people - you infect them with your vision and watch it spread.",
        group: ['Dark Virus']
      }
    ]
  },
  // NEW IMMEDIATE BRANCH QUESTIONS (Q4-Q6)
  {
    id: 'immediate-control-response-type',
    scenario: "An urgent threat appears on the horizon. No time for deliberation - you must act NOW. Your instinct kicks in. What's your immediate response style?",
    choices: [
      {
        text: "Swift aerial strike - Hit them from an unexpected angle. Rise above the situation, gain perspective, then strike with precision. Big, visible, decisive. One bold move that ends it cleanly. Like a crane diving or a bear charging - undeniable, overwhelming, immediate.",
        group: ['Light Crane', 'Dark Bear', 'Dark Rhino']
      },
      {
        text: "Agile maneuvering - Don't meet force with force. Dodge, adapt, strike from angles they don't expect. Quick, clever, hypnotic in your unpredictability. Whether you're nimbly escaping, cleverly tricking, hypnotically misdirecting, or spreading your influence rapidly.",
        group: ['Light Goat', 'Dark Monkey', 'Dark Snake', 'Dark Virus']
      }
    ]
  },
  {
    id: 'immediate-aerial-precision-vs-force',
    scenario: "You've committed to the bold strike. Now - how do you execute it? What does your decisive action look like?",
    choices: [
      {
        text: "Precise and swift - One perfect strike. Fast, clean, elegant. In and out before they know what happened. Minimal collateral damage, maximum efficiency. The crane diving for a fish - nothing wasted.",
        group: ['Light Crane']
      },
      {
        text: "Overwhelming force - Don't strike - dominate. Hit so hard there's no question of the outcome. Overwhelming, savage, absolute. Not elegant - inevitable. The bear asserting apex status.",
        group: ['Dark Bear']
      },
      {
        text: "Unstoppable momentum - Charge with such force that stopping isn't an option - for you or them. Build speed, commit fully, become an unstoppable object. The rhino at full charge - everything in the path gets cleared.",
        group: ['Dark Rhino']
      }
    ]
  },
  {
    id: 'immediate-agile-methodology',
    scenario: "You're maneuvering, staying mobile. The threat is trying to pin you down. How do you maintain your advantage while setting up your counter?",
    choices: [
      {
        text: "Nimble escape artist - Stay one step ahead through pure agility. When they zig, you zag. Fast footwork, quick thinking, always moving. Your advantage is speed and the ability to reach places they can't. Out-maneuver them completely.",
        group: ['Light Goat']
      },
      {
        text: "Clever trickster - Use misdirection and cleverness. Make them think you're here when you're there. Create diversions, exploit weaknesses, turn their strength against them. Win through wit, not force.",
        group: ['Dark Monkey']
      },
      {
        text: "Hypnotic control - Slow them down, confuse them, control their perception. Not through speed but through mesmerizing, serpentine unpredictability. They can't pin you down because they can't predict you. Slithering, sinuous, hypnotic.",
        group: ['Dark Snake']
      },
      {
        text: "Infectious persistence - Spread yourself. You're not in one place - you're in many places. They can't pin you down because you're multiplying your presence. Agile through proliferation, not speed.",
        group: ['Dark Virus']
      }
    ]
  },
  {
    id: 'immediate-influence-confrontation-style',
    scenario: "You need to influence someone immediately - no time for subtlety. They're about to make a terrible decision. How do you intervene?",
    choices: [
      {
        text: "Direct persuasion - Speak truth plainly. Whether it's wise counsel or chaotic disruption, you go straight at them with your message. Direct, immediate, personal. Look them in the eye and tell them what they need to hear.",
        group: ['Light Snake', 'Dark Goat']
      },
      {
        text: "Aggressive takeover - Don't persuade - dominate the conversation. Clever manipulation or infectious spread of your perspective until they can't think anything else. Aggressive influence that doesn't take no for an answer.",
        group: ['Dark Monkey', 'Dark Virus']
      }
    ]
  },
  {
    id: 'immediate-direct-persuasion-philosophy',
    scenario: "You've chosen direct confrontation. They're listening - barely. You have one chance to get through to them. What message do you deliver?",
    choices: [
      {
        text: "Transformative wisdom - Guide them toward enlightenment. Share hard truths with compassion. Help them see a path they couldn't see before. Transform their understanding through genuine wisdom. The mentor, the sage, the guide.",
        group: ['Light Snake']
      },
      {
        text: "Chaotic disruption - Shatter their assumptions. Expose the absurdity of their path. Use chaos, misdirection, uncomfortable truths. Don't guide them to the right path - destroy their confidence in the wrong one. Transform through confusion.",
        group: ['Dark Goat']
      }
    ]
  },
  {
    id: 'immediate-aggressive-influence-method',
    scenario: "You're taking over this conversation. They'll see it your way - one way or another. But how do you dominate their thinking?",
    choices: [
      {
        text: "Clever manipulation - Outsmart them. Present arguments they can't refute, find logical traps, use psychological tricks. Make them think it was their idea. Win through superior intellect and tactical maneuvering.",
        group: ['Dark Monkey']
      },
      {
        text: "Infectious persistence - Overwhelm them through sheer persistence and spread. Keep presenting your perspective from every angle until it infects their thinking. Not clever tricks - systematic conversion. Like a virus replicating until resistance is futile.",
        group: ['Dark Virus']
      }
    ]
  },
  {
    id: 'immediate-cohesion-unity-speed',
    scenario: "Your team is scattering under pressure. You need to bring them together RIGHT NOW - no time for careful planning. What's your immediate approach to restoring cohesion?",
    choices: [
      {
        text: "Agile coordination - Rally them through action. Move fast, lead by example, create momentum. They'll follow your speed and energy. Unity through synchronized movement - everybody keep up!",
        group: ['Light Goat']
      },
      {
        text: "Wise guidance - Even in crisis, offer perspective. Speak the truth that brings them back to purpose. Not fast - but profound. Unity through shared understanding, even if it takes a moment longer.",
        group: ['Light Snake']
      }
    ]
  },
  {
    id: 'immediate-careful-influence-patience',
    scenario: "You need immediate influence, but you're being careful about it. You've assessed the situation and know that hasty action could backfire. You're ready to act, but thoughtfully. What's your careful but immediate approach?",
    choices: [
      {
        text: "Patient manipulation - Act immediately, but with a plan. You've thought three steps ahead even while moving fast. Immediate action, long-term thinking. Deceptive, calculated, but executed NOW.",
        group: ['Dark Penguin']
      }
    ]
  },
  {
    id: 'immediate-careful-dark-penguin-identity',
    scenario: "You've positioned yourself perfectly. You're ready to act with careful, immediate influence. But what truly drives your methodology? What's at the core of your character?",
    choices: [
      {
        text: "Resilient deception - You endure, you persist, you outlast. Your manipulation isn't flashy - it's patient, stoic, enduring. Like a penguin surviving the harshest conditions, you use careful deception not for excitement, but for survival. You're in it for the long game, even when acting quickly.",
        group: ['Dark Penguin']
      }
    ]
  },
  {
    id: 'patient-cohesion-risky-sacrifice',
    scenario: "You have found your friend to be leaking information to an enemy. They are being blackmailed, but it will cost the lives of your fellow crew. What do you do?",
    choices: [
      {
        text: "Silently bring him back to safety and make it look like he was found out and dealt with — protect him without anyone knowing the truth",
        group: ['Light Fungi']
      },
      {
        text: "Bring him forth in front of everyone. We must keep honor, even if it means sacrificing friendship",
        group: ['Light Snake']
      },
      {
        text: "Feed him false information so the enemy grows weary keeping guard all night for a force that will strike them at their weakest",
        group: ['Dark Frog', 'Dark Fungi']
      }
    ]
  },
  {
    id: 'sacrifice-sabotage-split',
    scenario: "You chance upon a sea merchant that is supplying the food for the enemy army at a neutral port, if you make a decisive move, it could tip the battle in your direction!",
    choices: [
      {
        text: "Infest the ship with rats in the night, put them directly in to the food to spoil it all!",
        group: ['Dark Frog']
      },
      {
        text: "Pretend to be an envoy from the enemy, explain that they plan to kill him and take all his supplies because it's the last shipment they require before the last fight",
        group: ['Dark Fungi']
      }
    ]
  },
  {
    id: 'adaptive-influence-careful-inheritance',
    scenario: "You stand to make a sizable gain from an inheritance, should you show yourself worthy and honorable",
    choices: [
      {
        text: "Work with the group, an equitable outcome for all works better in the end",
        group: ['Light Octopus']
      },
      {
        text: "Be pleasant and patient, let the others spoil their reputation through bickering",
        group: ['Dark Penguin']
      }
    ]
  },
  {
    id: 'immediate-cohesion-careful-storm',
    scenario: "A violent storm hits while your group is weary from travel. An urgent message must reach the next town by morning.",
    choices: [
      {
        text: "Leave the team to bunker down in a cave and travel through the storm all night to get the message there in time, risk your own life for the sake of the group and message",
        group: ['Light Rhino']
      },
      {
        text: "Stay with the team overnight, organise resources, get a fire and water going, and set off together at first light when the storm ceases, hope you get there in time",
        group: ['Light Wolf']
      }
    ]
  }
,
  {
    id: 'patient-cohesion-flexible-tyrant',
    scenario: "The new king is a tyrant and life has become inhospitable and cruel for your kin, it cannot go on like this!",
    choices: [
      {
        text: "Openly defy them, a resistance cannot begin without a face and a message, risk yourself for the future",
        group: ['Light Bear', 'Light Rhino', 'Light Tree']
      },
      {
        text: "Build a secret network to overthrow them, no point in exposing your cards before you're ready to strike",
        group: ['Light Fungi', 'Light Snake', 'Light Octopus']
      },
      {
        text: "Work your way in to the tyrant's inner circle and play both sides, either way you can join the winning side when the rubble settles",
        group: ['Dark Frog', 'Dark Fungi']
      }
    ]
  },
  // Index 42 — splits Light Fungi / Light Snake / Light Octopus (path 1-3-2-2)
  {
    id: 'patient-cohesion-flexible-famine',
    scenario: "Crops have failed this season and winter approaches, there will not be enough food to go around, chaos and starvation are on the horizon if something isn't done!",
    choices: [
      {
        text: "Work on fixing the harvest and adding to the food supply, you have a small amount to add if you ration carefully",
        group: ['Light Fungi']
      },
      {
        text: "Venture off in to the wild, it will be dangerous in winter and will risk your life, but there is food out there",
        group: ['Light Snake']
      },
      {
        text: "Some of the wealthy townsfolk have extra stores, peacefully work with the community to help those most in need to make it through",
        group: ['Light Octopus']
      }
    ]
  }
];

/*
MULTI-QUESTION NARROWING SYSTEM:
- Question 1: Decision Style (Patient/Adaptive/Immediate)
- Question 2: Leadership Style (Control/Influence/Cohesion)
- Question 3: Risk Management (Risky/Adaptive/Careful)
- Question 4A: Tactical Approach (Infiltration/Destruction) - For Patient + Control + Risky
- Question 4B: Siege Strategy (Prepare/Escape/Bolster) - For Patient + Control + Adaptive

This creates 27+ possible outcome paths, with conditional Question 4s providing targeted splits
for specific groups that need further refinement.

The system automatically narrows down from 26 pieces to progressively smaller groups,
with many paths leading to single-piece outcomes through targeted follow-up questions.

Examples of branching Q4 outcomes:
- Patient + Control + Risky + Infiltration → Dark Octopus (1 piece)
- Patient + Control + Risky + Destruction → Dark Virus (1 piece)
- Patient + Control + Adaptive + Escape → Dark Octopus (1 piece)
- Patient + Control + Adaptive + Bolster → Dark Tree (1 piece)

The system handles conditional branching automatically based on current piece combinations.
*/

// Dev helper: checks if the quiz has a defined next question for a given piece set
// Mirrors the shouldContinue conditions in handleAnswer
const wouldQuizContinue = (pieces: string[], qIndex: number): boolean => {
  if (pieces.length <= 1) return false;
  const len = pieces.length;
  const has = (p: string) => pieces.includes(p);

  if (qIndex === 2) {
    return (
      (len === 2 && has('Dark Octopus') && has('Dark Virus')) ||
      (len === 5 && has('Light Bear') && has('Light Rhino') && has('Light Tree') && has('Dark Octopus') && has('Dark Virus')) ||
      (len === 5 && has('Light Bear') && has('Light Rhino') && has('Light Tree') && has('Dark Penguin') && has('Dark Tree')) ||
      (len === 9 && has('Light Fungi') && has('Light Snake') && has('Dark Frog') && has('Dark Fungi') && has('Dark Octopus') && has('Dark Virus') && has('Dark Wolf') && has('Dark Goat') && has('Light Virus')) ||
      (len === 10 && has('Light Fungi') && has('Light Octopus') && has('Light Snake') && has('Dark Frog') && has('Dark Fungi') && has('Dark Octopus') && has('Dark Virus') && has('Dark Wolf') && has('Dark Goat') && has('Light Virus')) ||
      (len === 3 && has('Light Frog') && has('Light Octopus') && has('Dark Penguin')) ||
      (len === 5 && has('Light Goat') && has('Dark Monkey') && has('Dark Octopus') && has('Dark Snake') && has('Dark Virus')) ||
      (len === 8 && has('Light Fungi') && has('Light Snake') && has('Dark Frog') && has('Dark Fungi') && has('Dark Goat') && has('Dark Octopus') && has('Dark Wolf') && has('Dark Virus')) ||
      (len === 7 && has('Light Crane') && has('Light Goat') && has('Dark Bear') && has('Dark Monkey') && has('Dark Rhino') && has('Dark Snake') && has('Dark Virus')) ||
      (len === 4 && has('Light Snake') && has('Dark Goat') && has('Dark Monkey') && has('Dark Virus')) ||
      (len === 2 && has('Light Goat') && has('Light Snake')) ||
      (len === 4 && has('Light Fungi') && has('Light Snake') && has('Dark Frog') && has('Dark Fungi')) ||
      (len === 2 && has('Light Octopus') && has('Dark Penguin')) ||
      (len === 2 && has('Light Rhino') && has('Light Wolf')) ||
      (len === 8 && has('Light Bear') && has('Light Fungi') && has('Light Octopus') && has('Light Rhino') && has('Light Snake') && has('Light Tree') && has('Dark Frog') && has('Dark Fungi'))
    );
  }
  if (qIndex === 3) {
    return (
      (len === 3 && has('Light Bear') && has('Light Tree') && has('Light Rhino')) ||
      (len === 2 && has('Dark Penguin') && has('Dark Tree')) ||
      (len === 3 && has('Dark Frog') && has('Dark Fungi') && has('Dark Virus')) ||
      (len === 5 && has('Dark Octopus') && has('Light Snake') && has('Light Virus') && has('Dark Wolf') && has('Dark Goat')) ||
      (len === 3 && has('Light Fungi') && has('Light Snake') && has('Light Virus')) ||
      (len === 3 && has('Light Octopus') && has('Dark Frog') && has('Dark Fungi')) ||
      (len === 4 && has('Dark Octopus') && has('Dark Wolf') && has('Dark Virus') && has('Dark Goat')) ||
      (len === 2 && has('Dark Frog') && has('Dark Fungi')) ||
      (len === 3 && has('Light Fungi') && has('Light Snake') && has('Light Octopus')) ||
      ((len >= 1 && len <= 3) && (has('Light Goat') || has('Dark Bear') || has('Dark Rhino'))) ||
      (len >= 2 && len <= 3 && (has('Light Fungi') || has('Dark Fungi') || has('Light Snake') || has('Dark Frog') || has('Dark Goat') || has('Dark Octopus') || has('Dark Wolf') || has('Dark Virus'))) ||
      ((len === 3 || len === 4) && (has('Light Crane') || has('Dark Bear') || has('Dark Rhino') || has('Light Goat') || has('Dark Monkey') || has('Dark Snake') || has('Dark Virus'))) ||
      (len === 2 && ((has('Light Snake') && has('Dark Goat')) || (has('Dark Monkey') && has('Dark Virus'))))
    );
  }
  if (qIndex === 4) {
    return (
      (len === 5 && has('Dark Octopus') && has('Light Snake') && has('Light Virus') && has('Dark Wolf') && has('Dark Goat')) ||
      (len === 2 && has('Light Snake') && has('Dark Goat')) ||
      (len === 2 && has('Dark Octopus') && has('Dark Wolf')) ||
      (len === 2 && has('Dark Bear') && has('Dark Rhino')) ||
      (len === 1 && has('Dark Penguin'))
    );
  }
  if (qIndex === 5) {
    return (
      (len === 2 && has('Dark Octopus') && has('Light Virus')) ||
      (len === 2 && has('Light Snake') && has('Dark Goat'))
    );
  }
  if (qIndex === 6) {
    return len === 2 && has('Light Snake') && has('Dark Goat');
  }
  return true; // Q1, Q2, Q7+: always continues
};

// Progress constellation config - 2D random-walk, centered after generation
const PROGRESS_PADDING = 24;        // min distance from viewBox edge
const PROGRESS_VB_W = 325;
const PROGRESS_VB_H = 220;
const PROGRESS_STEP_MIN = 55;       // longer strides so it actually spans the panel
const PROGRESS_STEP_MAX = 85;

function WhatsMyZodiac() {
  const navigate = useNavigate();
  const { user, userProfile, updateZodiacResult } = useAuthContext();
  const isDevMode = userProfile?.username === 'Dscvr';
  const containerRef = useRef<HTMLDivElement>(null);

  // Quiz logic state (unchanged)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [possiblePieces, setPossiblePieces] = useState<string[]>(zodiacAnimals);
  const [selectedTraits, setSelectedTraits] = useState<string[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<string>('');
  const [resultImageSrc, setResultImageSrc] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [showRevealText, setShowRevealText] = useState(false);
  const [showPieceReveal, setShowPieceReveal] = useState(false);
  // Celestial UI state
  const [selectedChoice, setSelectedChoice] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [answerHistory, setAnswerHistory] = useState<number[]>([]);
  const answeredCount = answerHistory.length;
  const [piecesHistory, setPiecesHistory] = useState<string[][]>([]);

  // Sky lightness shifts based on Light vs Dark piece balance
  const skyShift = useMemo(() => {
    const lightCount = possiblePieces.filter(p => p.startsWith('Light')).length;
    const darkCount = possiblePieces.filter(p => p.startsWith('Dark')).length;
    const total = lightCount + darkCount;
    if (total === 0) return 0;
    return (lightCount - darkCount) / total;
  }, [possiblePieces]);

  // Dynamic progress constellation - 2D random-walk, forward-biased, centered after generation
  const progressStars = useMemo(() => {
    type Star = { x: number; y: number; size: number };
    type Edge = { from: number; to: number };

    const totalStars = answeredCount + 1;
    const activeLength = Math.min(answerHistory.length, answeredCount);

    // Deterministic pseudo-random based on (i, salt). Same answer sequence -> same constellation.
    const seedRand = (a: number, b: number): number => {
      const s = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
      return s - Math.floor(s);
    };
    const starSize = (i: number) => 2.5 + seedRand(i, 3) * 2;

    // Forward-biased angle ranges — no backtracking, just directional spread
    //   choice 0 (top):    sweep upward-right
    //   choice 1 (middle): sweep roughly rightward with slight wander
    //   choice 2 (bottom): sweep downward-right
    const angleRangeForChoice = (choice: number): [number, number] => {
      if (choice === 0) return [-100, -20];
      if (choice === 1) return [-35, 35];
      return [20, 100];
    };

    // Generate raw positions starting at origin
    const stars: Star[] = [];
    let prevX = 0;
    let prevY = 0;
    stars.push({ x: 0, y: 0, size: starSize(0) });

    for (let i = 1; i < totalStars; i++) {
      const choice = i <= activeLength ? answerHistory[i - 1] : 1;
      const [aMin, aMax] = angleRangeForChoice(choice);
      const angleDeg = aMin + seedRand(i, 1) * (aMax - aMin);
      const stepLen = PROGRESS_STEP_MIN + seedRand(i, 2) * (PROGRESS_STEP_MAX - PROGRESS_STEP_MIN);
      const rad = (angleDeg * Math.PI) / 180;

      const nx = prevX + Math.cos(rad) * stepLen;
      const ny = prevY + Math.sin(rad) * stepLen;

      stars.push({ x: nx, y: ny, size: starSize(i) });
      prevX = nx;
      prevY = ny;
    }

    // Post-hoc: scale and center the constellation to fill the viewBox
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const s of stars) {
      if (s.x < minX) minX = s.x;
      if (s.x > maxX) maxX = s.x;
      if (s.y < minY) minY = s.y;
      if (s.y > maxY) maxY = s.y;
    }
    const rawW = maxX - minX || 1;
    const rawH = maxY - minY || 1;
    const availW = PROGRESS_VB_W - PROGRESS_PADDING * 2;
    const availH = PROGRESS_VB_H - PROGRESS_PADDING * 2;
    const scale = Math.min(availW / rawW, availH / rawH, 1.0); // never upscale beyond 1:1
    const centeredX = PROGRESS_PADDING + (availW - rawW * scale) / 2;
    const centeredY = PROGRESS_PADDING + (availH - rawH * scale) / 2;

    for (const s of stars) {
      s.x = centeredX + (s.x - minX) * scale;
      s.y = centeredY + (s.y - minY) * scale;
    }

    // Edges: simple chain only — no cross-connections (cleaner, like real constellations)
    const edges: Edge[] = [];
    for (let i = 1; i < stars.length; i++) {
      edges.push({ from: i - 1, to: i });
    }

    return { stars, edges };
  }, [answerHistory]);

  // Parallax mousemove effect
  useEffect(() => {
    const isTouch = 'ontouchstart' in window;
    if (isTouch) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const { clientWidth, clientHeight } = containerRef.current;
      const x = ((e.clientX / clientWidth) - 0.5) * 2;
      const y = ((e.clientY / clientHeight) - 0.5) * 2;
      containerRef.current.style.setProperty('--mouse-x', String(x * 8));
      containerRef.current.style.setProperty('--mouse-y', String(y * 5));
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleAnswer = (selectedGroup: string[], choiceText: string, choiceIndex: number) => {
    if (isProcessing) return;
    setIsProcessing(true);
    setSelectedChoice(choiceIndex);

    // Brief delay to show selection glow, then transition
    setTimeout(() => {
      setIsTransitioning(true);

      setTimeout(() => {
        setSelectedChoice(null);
        setIsTransitioning(false);
        setAnswerHistory(prev => [...prev.slice(0, currentQuestionIndex), choiceIndex]);

        // ---- Original quiz logic (unchanged) ----
        const traitMap: { [key: string]: string } = {
          "Hubris makes us act in rash ways, calm thoughful patience is best": "Patient",
          "Adapting and reacting to what changes you see as they come is best": "Adaptive",
          "React with self assurance and courage, the first to act has the advantage": "Reactive",
          "Too many chefs spoil the broth, a clear leader creates clarity and decisiveness": "Assertive",
          "Influence the best outcome, strengthen your group and weaken others for the best outcome": "Influential",
          "Work on the cohesion of the group, no one wins a war alone": "Cohesive",
          "If you have a good chance for a great outcome, take it! Fortune favours the bold": "Bold",
          "Some managed risks is always the best, being flexible is being unpredictable": "Flexible",
          "Acting rashly and risking yourself is foolish, slow and steady wins the race": "Cautious",
          "Work with another and dominate through advantages you both share": "Collaborative",
          "You have found an exploit that allows you to not let your opponents move, eventually leading to your complete victory through an exploit": "Exploitative",
          "Fight poison with poison - spread counter-rumors and undermine their credibility": "Toxic",
          "Slip away from the drama and keep yourself out of the toxic situation": "Evasive",
          "Take the blame yourself to protect your friends from further harm": "Sacrificial"
        };

        const trait = traitMap[choiceText];
        if (trait) {
          setSelectedTraits(prev => [...prev, trait]);
        }

        setPiecesHistory(prev => [...prev.slice(0, currentQuestionIndex), possiblePieces]);
        const newPossiblePieces = possiblePieces.filter(piece => selectedGroup.includes(piece));
        setPossiblePieces(newPossiblePieces);

        let shouldContinue = false;

        if (newPossiblePieces.length > 1) {
          if (currentQuestionIndex === 2) {
            const isPatientControlRisky = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Dark Octopus') &&
              newPossiblePieces.includes('Dark Virus');

            const isPatientControlAdaptive = newPossiblePieces.length === 5 &&
              newPossiblePieces.includes('Light Bear') &&
              newPossiblePieces.includes('Light Rhino') &&
              newPossiblePieces.includes('Light Tree') &&
              newPossiblePieces.includes('Dark Octopus') &&
              newPossiblePieces.includes('Dark Virus');

            const isPatientControlCareful = newPossiblePieces.length === 5 &&
              newPossiblePieces.includes('Light Bear') &&
              newPossiblePieces.includes('Light Rhino') &&
              newPossiblePieces.includes('Light Tree') &&
              newPossiblePieces.includes('Dark Penguin') &&
              newPossiblePieces.includes('Dark Tree');

            const isPatientInfluenceRisky = newPossiblePieces.length === 9 &&
              newPossiblePieces.includes('Light Fungi') &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Dark Frog') &&
              newPossiblePieces.includes('Dark Fungi') &&
              newPossiblePieces.includes('Dark Octopus') &&
              newPossiblePieces.includes('Dark Virus') &&
              newPossiblePieces.includes('Dark Wolf') &&
              newPossiblePieces.includes('Dark Goat') &&
              newPossiblePieces.includes('Light Virus');

            const isPatientInfluenceAdaptive = newPossiblePieces.length === 10 &&
              newPossiblePieces.includes('Light Fungi') &&
              newPossiblePieces.includes('Light Octopus') &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Dark Frog') &&
              newPossiblePieces.includes('Dark Fungi') &&
              newPossiblePieces.includes('Dark Octopus') &&
              newPossiblePieces.includes('Dark Virus') &&
              newPossiblePieces.includes('Dark Wolf') &&
              newPossiblePieces.includes('Dark Goat') &&
              newPossiblePieces.includes('Light Virus');

            const isPatientInfluenceCareful = newPossiblePieces.length === 3 &&
              newPossiblePieces.includes('Light Frog') &&
              newPossiblePieces.includes('Light Octopus') &&
              newPossiblePieces.includes('Dark Penguin');

            const isAdaptiveControlRisky = newPossiblePieces.length === 5 &&
              newPossiblePieces.includes('Light Goat') &&
              newPossiblePieces.includes('Dark Monkey') &&
              newPossiblePieces.includes('Dark Octopus') &&
              newPossiblePieces.includes('Dark Snake') &&
              newPossiblePieces.includes('Dark Virus');

            const isAdaptiveInfluenceRisky = newPossiblePieces.length === 8 &&
              newPossiblePieces.includes('Light Fungi') &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Dark Frog') &&
              newPossiblePieces.includes('Dark Fungi') &&
              newPossiblePieces.includes('Dark Goat') &&
              newPossiblePieces.includes('Dark Octopus') &&
              newPossiblePieces.includes('Dark Wolf') &&
              newPossiblePieces.includes('Dark Virus');

            const isImmediateControlRisky = newPossiblePieces.length === 7 &&
              newPossiblePieces.includes('Light Crane') &&
              newPossiblePieces.includes('Light Goat') &&
              newPossiblePieces.includes('Dark Bear') &&
              newPossiblePieces.includes('Dark Monkey') &&
              newPossiblePieces.includes('Dark Rhino') &&
              newPossiblePieces.includes('Dark Snake') &&
              newPossiblePieces.includes('Dark Virus');

            const isImmediateInfluenceRisky = newPossiblePieces.length === 4 &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Dark Goat') &&
              newPossiblePieces.includes('Dark Monkey') &&
              newPossiblePieces.includes('Dark Virus');

            const isImmediateCohesionRisky = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Light Goat') &&
              newPossiblePieces.includes('Light Snake');

            const isImmediateInfluenceCareful = newPossiblePieces.length === 1 &&
              newPossiblePieces.includes('Dark Penguin');

            const isPatientCohesionRisky = newPossiblePieces.length === 4 &&
              newPossiblePieces.includes('Light Fungi') &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Dark Frog') &&
              newPossiblePieces.includes('Dark Fungi');

            const isAdaptiveInfluenceCareful = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Light Octopus') &&
              newPossiblePieces.includes('Dark Penguin');

            const isImmediateCohesionCareful = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Light Rhino') &&
              newPossiblePieces.includes('Light Wolf');

            const isPatientCohesionFlexible = newPossiblePieces.length === 8 &&
              newPossiblePieces.includes('Light Bear') &&
              newPossiblePieces.includes('Light Fungi') &&
              newPossiblePieces.includes('Light Octopus') &&
              newPossiblePieces.includes('Light Rhino') &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Light Tree') &&
              newPossiblePieces.includes('Dark Frog') &&
              newPossiblePieces.includes('Dark Fungi');

            shouldContinue = (
              isPatientControlRisky || isPatientControlAdaptive || isPatientControlCareful ||
              isPatientInfluenceRisky || isPatientInfluenceAdaptive || isPatientInfluenceCareful ||
              isPatientCohesionRisky ||
              isAdaptiveControlRisky || isAdaptiveInfluenceRisky || isAdaptiveInfluenceCareful ||
              isImmediateControlRisky || isImmediateInfluenceRisky || isImmediateCohesionRisky ||
              isImmediateCohesionCareful || isImmediateInfluenceCareful ||
              isPatientCohesionFlexible
            ) && currentQuestionIndex < questions.length - 1;
          } else if (currentQuestionIndex === 3) {
            const needsQ5LightGroup = newPossiblePieces.length === 3 &&
              newPossiblePieces.includes('Light Bear') &&
              newPossiblePieces.includes('Light Tree') &&
              newPossiblePieces.includes('Light Rhino');

            const needsQ5DarkGroup = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Dark Penguin') &&
              newPossiblePieces.includes('Dark Tree');

            const needsQ5PoisonGroup = newPossiblePieces.length === 3 &&
              newPossiblePieces.includes('Dark Frog') &&
              newPossiblePieces.includes('Dark Fungi') &&
              newPossiblePieces.includes('Dark Virus');

            const needsQ5EscapeGroup = newPossiblePieces.length === 5 &&
              newPossiblePieces.includes('Dark Octopus') &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Light Virus') &&
              newPossiblePieces.includes('Dark Wolf') &&
              newPossiblePieces.includes('Dark Goat');

            const needsQ5AltruisticGroup = newPossiblePieces.length === 3 &&
              newPossiblePieces.includes('Light Fungi') &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Light Virus');

            const needsQ5JusticeGroup = newPossiblePieces.length === 3 &&
              newPossiblePieces.includes('Light Octopus') &&
              newPossiblePieces.includes('Dark Frog') &&
              newPossiblePieces.includes('Dark Fungi');

            const needsQ5SelfServingGroup = newPossiblePieces.length === 4 &&
              newPossiblePieces.includes('Dark Octopus') &&
              newPossiblePieces.includes('Dark Wolf') &&
              newPossiblePieces.includes('Dark Virus') &&
              newPossiblePieces.includes('Dark Goat');

            const needsQ5SabotageSplit = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Dark Frog') &&
              newPossiblePieces.includes('Dark Fungi');

            const needsQ5ConnectorGroup = newPossiblePieces.length === 3 &&
              newPossiblePieces.includes('Light Fungi') &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Light Octopus');

            const needsQ5AdaptiveTerritorial = (newPossiblePieces.length === 3 || newPossiblePieces.length === 2 || newPossiblePieces.length === 1) &&
              (newPossiblePieces.includes('Light Goat') || newPossiblePieces.includes('Dark Bear') || newPossiblePieces.includes('Dark Rhino'));

            const needsQ5AdaptiveInfluence = newPossiblePieces.length >= 2 && newPossiblePieces.length <= 3 &&
              (newPossiblePieces.includes('Light Fungi') || newPossiblePieces.includes('Dark Fungi') ||
               newPossiblePieces.includes('Light Snake') || newPossiblePieces.includes('Dark Frog') || newPossiblePieces.includes('Dark Goat') ||
               newPossiblePieces.includes('Dark Octopus') || newPossiblePieces.includes('Dark Wolf') || newPossiblePieces.includes('Dark Virus'));

            const needsQ5ImmediateControl = (newPossiblePieces.length === 3 || newPossiblePieces.length === 4) &&
              (newPossiblePieces.includes('Light Crane') || newPossiblePieces.includes('Dark Bear') || newPossiblePieces.includes('Dark Rhino') ||
               newPossiblePieces.includes('Light Goat') || newPossiblePieces.includes('Dark Monkey') || newPossiblePieces.includes('Dark Snake') || newPossiblePieces.includes('Dark Virus'));

            const needsQ5ImmediateInfluence = newPossiblePieces.length === 2 &&
              ((newPossiblePieces.includes('Light Snake') && newPossiblePieces.includes('Dark Goat')) ||
               (newPossiblePieces.includes('Dark Monkey') && newPossiblePieces.includes('Dark Virus')));

            const isSingleResult = newPossiblePieces.length === 1;

            shouldContinue = (
              needsQ5LightGroup || needsQ5DarkGroup || needsQ5PoisonGroup || needsQ5EscapeGroup ||
              needsQ5AltruisticGroup || needsQ5JusticeGroup || needsQ5SelfServingGroup ||
              needsQ5SabotageSplit || needsQ5ConnectorGroup ||
              needsQ5AdaptiveTerritorial || needsQ5AdaptiveInfluence ||
              needsQ5ImmediateControl || needsQ5ImmediateInfluence
            ) && !isSingleResult && currentQuestionIndex < questions.length - 1;
          } else if (currentQuestionIndex === 4) {
            const needsQ6BattlefieldGroup = newPossiblePieces.length === 5 &&
              newPossiblePieces.includes('Dark Octopus') &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Light Virus') &&
              newPossiblePieces.includes('Dark Wolf') &&
              newPossiblePieces.includes('Dark Goat');

            const needsQ8ShameGroup = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Dark Goat');

            const needsQ6ShadowGroup = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Dark Octopus') &&
              newPossiblePieces.includes('Dark Wolf');

            const needsQ6DarkBearRhino = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Dark Bear') &&
              newPossiblePieces.includes('Dark Rhino');

            const needsQ6DarkPenguin = newPossiblePieces.length === 1 &&
              newPossiblePieces.includes('Dark Penguin');

            const isSingleResult = newPossiblePieces.length === 1 && !needsQ6DarkPenguin;
            shouldContinue = (needsQ6BattlefieldGroup || needsQ8ShameGroup || needsQ6ShadowGroup || needsQ6DarkBearRhino || needsQ6DarkPenguin) && !isSingleResult && currentQuestionIndex < questions.length - 1;
          } else if (currentQuestionIndex === 5) {
            const needsQ7CellGroup = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Dark Octopus') &&
              newPossiblePieces.includes('Light Virus');

            const needsQ8ShameGroup = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Dark Goat');

            const isSingleResult = newPossiblePieces.length === 1;
            shouldContinue = (needsQ7CellGroup || needsQ8ShameGroup) && !isSingleResult && currentQuestionIndex < questions.length - 1;
          } else if (currentQuestionIndex === 6) {
            const needsQ8ShameGroup = newPossiblePieces.length === 2 &&
              newPossiblePieces.includes('Light Snake') &&
              newPossiblePieces.includes('Dark Goat');

            const isSingleResult = newPossiblePieces.length === 1;
            shouldContinue = needsQ8ShameGroup && !isSingleResult && currentQuestionIndex < questions.length - 1;
          } else {
            shouldContinue = currentQuestionIndex < questions.length - 1;
          }
        }

        if (shouldContinue) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          const finalResult = newPossiblePieces.length > 0
            ? newPossiblePieces[Math.floor(Math.random() * newPossiblePieces.length)]
            : 'Light Bear';
          const imageSrc = getPieceImage(finalResult);
          setResult(finalResult);
          setResultImageSrc(imageSrc);

          if (user) {
            updateZodiacResult(finalResult).catch(() => {});
          }

          startRevealSequence(finalResult);
        }

        setTimeout(() => setIsProcessing(false), 100);
        // ---- End original quiz logic ----
      }, 300);
    }, 200);
  };

  const startRevealSequence = useCallback((_finalResult: string) => {
    setIsRevealing(true);
    setShowResult(true);
    setShowRevealText(true);

    // Brief "Discovering..." moment, then reveal the piece
    setTimeout(() => {
      setShowPieceReveal(true);
    }, 1800);
  }, []);

  // Fallback: if no more questions or only one piece remains, trigger result
  useEffect(() => {
    if (!showResult && !isRevealing &&
        (currentQuestionIndex >= questions.length || possiblePieces.length <= 1)) {
      const finalResult = possiblePieces.length > 0 ? possiblePieces[0] : 'Light Bear';
      const imageSrc = getPieceImage(finalResult);
      setResult(finalResult);
      setResultImageSrc(imageSrc);
      if (user) {
        updateZodiacResult(finalResult).catch(() => {});
      }
      startRevealSequence(finalResult);
    }
  }, [currentQuestionIndex, possiblePieces, showResult, isRevealing, user, updateZodiacResult, startRevealSequence]);

  // Navigate back one question (used by result screen + dev panel)
  const handleGoBack = () => {
    if (showResult || isRevealing) {
      setShowResult(false);
      setResult('');
      setIsRevealing(false);
      setShowPieceReveal(false);
      setResultImageSrc('');
    }
    if (currentQuestionIndex > 0 && piecesHistory.length > 0) {
      const prevPieces = piecesHistory[currentQuestionIndex - 1] || zodiacAnimals;
      setPossiblePieces(prevPieces);
      setPiecesHistory(prev => prev.slice(0, -1));
      setAnswerHistory(prev => prev.slice(0, -1));
      setCurrentQuestionIndex(prev => prev - 1);
      setSelectedTraits(prev => prev.slice(0, -1));
    }
  };

  const devGoForward = (choices: { group: string[]; text: string }[]) => {
    const prevChoice = answerHistory[currentQuestionIndex];
    if (prevChoice === undefined) return;
    const choice = choices[prevChoice];
    if (!choice) return;
    setPiecesHistory(prev => [...prev.slice(0, currentQuestionIndex), possiblePieces]);
    const newPossiblePieces = possiblePieces.filter(piece => choice.group.includes(piece));
    setPossiblePieces(newPossiblePieces);
    setCurrentQuestionIndex(prev => prev + 1);
  };

  const customDescription = customDescriptions[result];

  // ---- Shared background layers ----
  const renderBackground = () => (
    <>
      <div className={styles.starfieldLayer1} />
      <div className={styles.starfieldLayer2} />
      <div className={styles.starfieldLayer3} />
      <div className={styles.shootingStar1} />
      <div className={styles.shootingStar2} />
      <div className={styles.shootingStar3} />
      <div className={styles.shootingStar4} />
      <div className={styles.shootingStar5} />
    </>
  );

  const renderProgressConstellation = (additionalClassName?: string) => {
    const { stars, edges } = progressStars;
    // Deterministic twinkle delay per star
    const twinkleDelay = (i: number) => {
      const s = Math.sin(i * 12.9898 + 7 * 78.233) * 43758.5453;
      return ((s - Math.floor(s)) * 4).toFixed(2); // 0–4s stagger
    };
    return (
      <svg
        className={`${styles.starProgress} ${additionalClassName || ''}`}
        viewBox="0 0 325 220"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Edges first (stars draw on top) */}
        {edges.map((e, idx) => {
          const a = stars[e.from];
          const b = stars[e.to];
          const bothAnswered = e.from < answeredCount && e.to < answeredCount;
          return (
            <line
              key={`e${idx}`}
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              className={`${styles.starProgressLine} ${bothAnswered ? styles.active : ''}`}
            />
          );
        })}
        {/* Stars — small circles with layered glow halos */}
        {stars.map((star, i) => {
          const isLast = i === stars.length - 1;
          const isAnswered = i < answeredCount;
          const isCurrent = i === answeredCount && !showResult;
          const isEndpoint = isLast && (isAnswered || showResult);
          const r = isEndpoint ? 3.5 : (1.5 + star.size * 0.4);
          return (
            <circle
              key={`s${i}`}
              cx={star.x} cy={star.y} r={r}
              className={`${styles.starProgressPoint} ${
                isAnswered ? styles.answered : ''
              } ${isCurrent ? styles.current : ''} ${
                isEndpoint ? styles.endpoint : ''
              }`}
              style={{
                animationDelay: isAnswered && !isCurrent ? `${twinkleDelay(i)}s` : undefined,
              }}
            />
          );
        })}
      </svg>
    );
  };

  // ---- RESULT SCREEN ----
  if (showResult && result) {
    return (
      <div className={styles.container} ref={containerRef} style={{ '--sky-shift': skyShift } as React.CSSProperties}>
        <GlobalHeader />
        {renderBackground()}

        <div className={styles.headerSection}>
          <button
            onClick={() => navigate('/main')}
            className={styles.homeButton}
            title="Return to Main Page"
          >
            Home
          </button>
          <h1 className={styles.title}>Your Zodiac</h1>
        </div>

        {/* Player's journey constellation */}
        {showPieceReveal && (
          <div className={`${styles.journeyConstellation} ${styles.fadeInDescription}`}>
            {renderProgressConstellation(styles.resultProgress)}
          </div>
        )}

        <div className={styles.resultContainer}>
          <div className={styles.resultCard}>
            {isRevealing && !showPieceReveal ? (
              <div className={styles.revealSequence}>
                {showRevealText && (
                  <h2 className={styles.revealText}>Discovering...</h2>
                )}
                <div className={styles.revealCircle}>
                  <div className={styles.circleContent}></div>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.resultEmoji}>
                  <img
                    src={resultImageSrc}
                    alt={result}
                    className={`${styles.pieceImage} ${showPieceReveal ? styles.pieceMaterialize : ''}`}
                  />
                </div>
                <h2 className={`${styles.resultTitle} ${showPieceReveal ? styles.fadeInTitle : ''}`}>
                  {result}
                </h2>

                {customDescription && showPieceReveal ? (
                  <div className={`${styles.customDescription} ${styles.fadeInDescription}`}>
                    <p>{customDescription}</p>
                  </div>
                ) : null}

                {showPieceReveal && (
                  <div className={`${styles.resultActions} ${styles.fadeInActions}`}>
                    {isDevMode && (
                      <button onClick={handleGoBack} className={styles.backArrow} title="Previous question">
                        &#9664;
                      </button>
                    )}
                    <button onClick={() => navigate('/main')} className={styles.backButton}>
                      Back
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

      </div>
    );
  }

  // Determine which question to show
  let currentQuestion = questions[currentQuestionIndex];

  // If we're at Q4, determine which specific Q4 based on current pieces
  if (currentQuestionIndex === 3) {
    const isPatientControlRisky = possiblePieces.length === 2 &&
      possiblePieces.includes('Dark Octopus') &&
      possiblePieces.includes('Dark Virus');

    const isPatientControlCareful = possiblePieces.length === 5 &&
      possiblePieces.includes('Light Bear') &&
      possiblePieces.includes('Light Rhino') &&
      possiblePieces.includes('Light Tree') &&
      possiblePieces.includes('Dark Penguin') &&
      possiblePieces.includes('Dark Tree');

    const isPatientInfluenceRisky = possiblePieces.length === 9 &&
      possiblePieces.includes('Light Fungi') &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Dark Frog') &&
      possiblePieces.includes('Dark Fungi') &&
      possiblePieces.includes('Dark Octopus') &&
      possiblePieces.includes('Dark Virus') &&
      possiblePieces.includes('Dark Wolf') &&
      possiblePieces.includes('Dark Goat') &&
      possiblePieces.includes('Light Virus');

    const isPatientInfluenceAdaptive = possiblePieces.length === 10 &&
      possiblePieces.includes('Light Fungi') &&
      possiblePieces.includes('Light Octopus') &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Dark Frog') &&
      possiblePieces.includes('Dark Fungi') &&
      possiblePieces.includes('Dark Octopus') &&
      possiblePieces.includes('Dark Virus') &&
      possiblePieces.includes('Dark Wolf') &&
      possiblePieces.includes('Dark Goat') &&
      possiblePieces.includes('Light Virus');

    const isPatientInfluenceCareful = possiblePieces.length === 3 &&
      possiblePieces.includes('Light Frog') &&
      possiblePieces.includes('Light Octopus') &&
      possiblePieces.includes('Dark Penguin');

    const isAdaptiveControlRisky = possiblePieces.length === 5 &&
      possiblePieces.includes('Light Goat') &&
      possiblePieces.includes('Dark Monkey') &&
      possiblePieces.includes('Dark Octopus') &&
      possiblePieces.includes('Dark Snake') &&
      possiblePieces.includes('Dark Virus');

    const isAdaptiveInfluenceRisky = possiblePieces.length === 8 &&
      possiblePieces.includes('Light Fungi') &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Dark Frog') &&
      possiblePieces.includes('Dark Fungi') &&
      possiblePieces.includes('Dark Goat') &&
      possiblePieces.includes('Dark Octopus') &&
      possiblePieces.includes('Dark Wolf') &&
      possiblePieces.includes('Dark Virus');

    const isImmediateControlRisky = possiblePieces.length === 7 &&
      possiblePieces.includes('Light Crane') &&
      possiblePieces.includes('Light Goat') &&
      possiblePieces.includes('Dark Bear') &&
      possiblePieces.includes('Dark Monkey') &&
      possiblePieces.includes('Dark Rhino') &&
      possiblePieces.includes('Dark Snake') &&
      possiblePieces.includes('Dark Virus');

    const isImmediateInfluenceRisky = possiblePieces.length === 4 &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Dark Goat') &&
      possiblePieces.includes('Dark Monkey') &&
      possiblePieces.includes('Dark Virus');

    const isPatientCohesionRisky = possiblePieces.length === 4 &&
      possiblePieces.includes('Light Fungi') &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Dark Frog') &&
      possiblePieces.includes('Dark Fungi');

    const isAdaptiveInfluenceCareful = possiblePieces.length === 2 &&
      possiblePieces.includes('Light Octopus') &&
      possiblePieces.includes('Dark Penguin');

    const isImmediateCohesionCareful = possiblePieces.length === 2 &&
      possiblePieces.includes('Light Rhino') &&
      possiblePieces.includes('Light Wolf');

    const isPatientCohesionFlexible = possiblePieces.length === 8 &&
      possiblePieces.includes('Light Bear') &&
      possiblePieces.includes('Light Fungi') &&
      possiblePieces.includes('Light Octopus') &&
      possiblePieces.includes('Light Rhino') &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Light Tree') &&
      possiblePieces.includes('Dark Frog') &&
      possiblePieces.includes('Dark Fungi');

    if (isPatientControlRisky) {
      currentQuestion = questions[3];
    } else if (isPatientControlCareful) {
      currentQuestion = questions[5];
    } else if (isPatientInfluenceRisky) {
      currentQuestion = questions[8];
    } else if (isPatientInfluenceAdaptive) {
      currentQuestion = questions[13];
    } else if (isPatientInfluenceCareful) {
      currentQuestion = questions[18];
    } else if (isAdaptiveControlRisky) {
      currentQuestion = questions[21];
    } else if (isAdaptiveInfluenceRisky) {
      currentQuestion = questions[24];
    } else if (isImmediateControlRisky) {
      currentQuestion = questions[28];
    } else if (isImmediateInfluenceRisky) {
      currentQuestion = questions[31];
    } else if (isPatientCohesionRisky) {
      currentQuestion = questions[37];
    } else if (isAdaptiveInfluenceCareful) {
      currentQuestion = questions[39];
    } else if (isImmediateCohesionCareful) {
      currentQuestion = questions[40];
    } else if (isPatientCohesionFlexible) {
      currentQuestion = questions[41];
    } else {
      currentQuestion = questions[4];
    }
  } else if (currentQuestionIndex === 4) {
    const isLightGroup = possiblePieces.length === 3 &&
      possiblePieces.includes('Light Bear') &&
      possiblePieces.includes('Light Tree') &&
      possiblePieces.includes('Light Rhino');

    const isDarkGroup = possiblePieces.length === 2 &&
      possiblePieces.includes('Dark Penguin') &&
      possiblePieces.includes('Dark Tree');

    const isPoisonGroup = possiblePieces.length === 3 &&
      possiblePieces.includes('Dark Frog') &&
      possiblePieces.includes('Dark Fungi') &&
      possiblePieces.includes('Dark Virus');

    const isEscapeGroup = possiblePieces.length === 5 &&
      possiblePieces.includes('Dark Octopus') &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Light Virus') &&
      possiblePieces.includes('Dark Wolf') &&
      possiblePieces.includes('Dark Goat');

    const isAltruisticGroup = possiblePieces.length === 3 &&
      possiblePieces.includes('Light Fungi') &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Light Virus');

    const isJusticeGroup = possiblePieces.length === 3 &&
      possiblePieces.includes('Light Octopus') &&
      possiblePieces.includes('Dark Frog') &&
      possiblePieces.includes('Dark Fungi');

    const isSelfServingGroup = possiblePieces.length === 4 &&
      possiblePieces.includes('Dark Octopus') &&
      possiblePieces.includes('Dark Wolf') &&
      possiblePieces.includes('Dark Virus') &&
      possiblePieces.includes('Dark Goat');

    const isAdaptiveTerritorial = (possiblePieces.length === 3 || possiblePieces.length === 2) &&
      (possiblePieces.includes('Light Goat') || possiblePieces.includes('Dark Bear') || possiblePieces.includes('Dark Rhino'));

    const isAdaptiveLifeForce = possiblePieces.length === 2 &&
      possiblePieces.includes('Light Fungi') &&
      possiblePieces.includes('Dark Fungi');

    const isAdaptiveSocial = possiblePieces.length === 3 &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Dark Frog') &&
      possiblePieces.includes('Dark Goat');

    const isAdaptivePower = possiblePieces.length === 3 &&
      possiblePieces.includes('Dark Octopus') &&
      possiblePieces.includes('Dark Wolf') &&
      possiblePieces.includes('Dark Virus');

    const isImmediateAerial = possiblePieces.length === 3 &&
      possiblePieces.includes('Light Crane') &&
      possiblePieces.includes('Dark Bear') &&
      possiblePieces.includes('Dark Rhino');

    const isImmediateAgile = possiblePieces.length === 4 &&
      possiblePieces.includes('Light Goat') &&
      possiblePieces.includes('Dark Monkey') &&
      possiblePieces.includes('Dark Snake') &&
      possiblePieces.includes('Dark Virus');

    const isImmediateDirectPersuasion = possiblePieces.length === 2 &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Dark Goat');

    const isImmediateAggressiveTakeover = possiblePieces.length === 2 &&
      possiblePieces.includes('Dark Monkey') &&
      possiblePieces.includes('Dark Virus');

    const isImmediateCohesion = possiblePieces.length === 2 &&
      possiblePieces.includes('Light Goat') &&
      possiblePieces.includes('Light Snake');

    const isSabotageSplit = possiblePieces.length === 2 &&
      possiblePieces.includes('Dark Frog') &&
      possiblePieces.includes('Dark Fungi');

    const isConnectorGroup = possiblePieces.length === 3 &&
      possiblePieces.includes('Light Fungi') &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Light Octopus');

    if (isLightGroup) {
      currentQuestion = questions[6];
    } else if (isDarkGroup) {
      currentQuestion = questions[7];
    } else if (isPoisonGroup) {
      currentQuestion = questions[9];
    } else if (isEscapeGroup) {
      currentQuestion = questions[10];
    } else if (isAltruisticGroup) {
      currentQuestion = questions[14];
    } else if (isJusticeGroup) {
      currentQuestion = questions[15];
    } else if (isSelfServingGroup) {
      currentQuestion = questions[16];
    } else if (isAdaptiveTerritorial) {
      currentQuestion = questions[22];
    } else if (isAdaptiveLifeForce) {
      currentQuestion = questions[25];
    } else if (isAdaptiveSocial) {
      currentQuestion = questions[26];
    } else if (isAdaptivePower) {
      currentQuestion = questions[27];
    } else if (isImmediateAerial) {
      currentQuestion = questions[29];
    } else if (isImmediateAgile) {
      currentQuestion = questions[30];
    } else if (isImmediateDirectPersuasion) {
      currentQuestion = questions[32];
    } else if (isImmediateAggressiveTakeover) {
      currentQuestion = questions[33];
    } else if (isImmediateCohesion) {
      currentQuestion = questions[34];
    } else if (isSabotageSplit) {
      currentQuestion = questions[38];
    } else if (isConnectorGroup) {
      currentQuestion = questions[42];
    }
  } else if (currentQuestionIndex === 5) {
    const isBattlefieldGroup = possiblePieces.length === 5 &&
      possiblePieces.includes('Dark Octopus') &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Light Virus') &&
      possiblePieces.includes('Dark Wolf') &&
      possiblePieces.includes('Dark Goat');

    const isShameGroup = possiblePieces.length === 2 &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Dark Goat');

    const isShadowGroup = possiblePieces.length === 2 &&
      possiblePieces.includes('Dark Octopus') &&
      possiblePieces.includes('Dark Wolf');

    const isDarkBearRhino = possiblePieces.length === 2 &&
      possiblePieces.includes('Dark Bear') &&
      possiblePieces.includes('Dark Rhino');

    const isDarkPenguinSolo = possiblePieces.length === 1 &&
      possiblePieces.includes('Dark Penguin');

    if (isBattlefieldGroup) {
      currentQuestion = questions[10];
    } else if (isShameGroup) {
      currentQuestion = questions[12];
    } else if (isShadowGroup) {
      currentQuestion = questions[17];
    } else if (isDarkBearRhino) {
      currentQuestion = questions[23];
    } else if (isDarkPenguinSolo) {
      currentQuestion = questions[35];
    }
  } else if (currentQuestionIndex === 6) {
    const isDarkPenguinFinal = possiblePieces.length === 1 &&
      possiblePieces.includes('Dark Penguin');

    if (isDarkPenguinFinal) {
      currentQuestion = questions[36];
    } else {
      const isCellGroup = possiblePieces.length === 2 &&
        possiblePieces.includes('Dark Octopus') &&
        possiblePieces.includes('Light Virus');

      const isShameGroup = possiblePieces.length === 2 &&
        possiblePieces.includes('Light Snake') &&
        possiblePieces.includes('Dark Goat');

      if (isCellGroup) {
        currentQuestion = questions[11];
      } else if (isShameGroup) {
        currentQuestion = questions[12];
      }
    }
  } else if (currentQuestionIndex === 7) {
    const isShameGroup = possiblePieces.length === 2 &&
      possiblePieces.includes('Light Snake') &&
      possiblePieces.includes('Dark Goat');

    if (isShameGroup) {
      currentQuestion = questions[12];
    }
  }

  // ---- QUIZ SCREEN ----
  return (
    <div className={styles.container} ref={containerRef} style={{ '--sky-shift': skyShift } as React.CSSProperties}>
      <GlobalHeader />
      {renderBackground()}

      <div className={styles.headerSection}>
        <button
          onClick={() => navigate('/main')}
          className={styles.homeButton}
          title="Return to Main Page"
        >
          Home
        </button>
        <h1 className={styles.title}>What's My Zodiac?</h1>
      </div>

      <div className={styles.quizContainer}>
        {/* Dynamic constellation progress indicator */}
        {renderProgressConstellation()}

        {/* Question card */}
        <div className={`${styles.questionCard} ${
          isTransitioning ? styles.exiting : styles.entering
        }`}>
          <div className={styles.questionNumber}>
            Question {answeredCount + 1}
          </div>
          <h2 className={styles.questionText}>{currentQuestion?.scenario}</h2>
          <div className={styles.optionsContainer}>
            {currentQuestion?.choices.map((choice, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(choice.group, choice.text, index)}
                className={`${styles.optionButton} ${
                  selectedChoice === index ? styles.selected : ''
                }`}
                disabled={isProcessing}
              >
                {choice.text}
              </button>
            ))}
          </div>

        </div>
      </div>

      {isDevMode && currentQuestion && (
        <div className={styles.devCheatSheet}>
          <div className={styles.devHeader}>DEV</div>
          <div className={styles.devPath}>
            {answerHistory.length > 0
              ? answerHistory.map((c, i) => `${c + 1}`).join('-')
              : '—'}
          </div>
          <div className={styles.devNav}>
            <button onClick={handleGoBack} disabled={currentQuestionIndex === 0}>&#9664;</button>
            <button onClick={() => devGoForward(currentQuestion.choices)} disabled={answerHistory.length <= currentQuestionIndex}>&#9654;</button>
          </div>
          <div className={styles.devPieceCount}>{possiblePieces.length} pieces</div>
          <div className={styles.devChoices}>
            {currentQuestion.choices.map((choice, i) => {
              const remaining = possiblePieces.filter(p => choice.group.includes(p));
              const light = remaining.filter(p => p.startsWith('Light')).length;
              const dark = remaining.filter(p => p.startsWith('Dark')).length;
              const isComplete = remaining.length === 1;
              const isIncomplete = remaining.length > 1 && !wouldQuizContinue(remaining, currentQuestionIndex);
              return (
                <div key={i} className={`${styles.devChoiceItem} ${isIncomplete ? styles.devIncomplete : ''} ${isComplete ? styles.devComplete : ''}`}>
                  <div className={styles.devChoiceHeader}>
                    {isComplete ? '\u2713 ' : isIncomplete ? '\u2717 ' : ''}{i + 1}: {light}L/{dark}D ({remaining.length})
                    {isIncomplete && <span className={styles.devIncompleteTag}>DEAD END</span>}
                  </div>
                  <div className={styles.devChoicePieces}>
                    {remaining.map(p => p.replace('Light ', 'L\u00b7').replace('Dark ', 'D\u00b7')).join(', ')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default WhatsMyZodiac;
